import AssetLockerPlacement from './AssetLockerPlacement';
import React, { useState } from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Autocomplete,
  Chip,
  InputAdornment,
  Tooltip
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CategoryIcon from "@mui/icons-material/Category";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LabelIcon from "@mui/icons-material/Label";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import InventoryIcon from "@mui/icons-material/Inventory";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InfoIcon from "@mui/icons-material/Info";
import { formatDateForInput } from "../utils/assetHelpers";
import { DEFAULT_CATEGORIES, DEFAULT_DISCOUNTS } from "../utils/assetConstants";

const AssetBasicInfo = ({ 
  asset, 
  onChange, 
  statuses = [], 
  rentalPoints = [], 
  tariffs = [],
  onCreateStatus,
  onCreateRentalPoint,
  onCreateTariff,
  separateSections = false
}) => {
  const [copyResult, setCopyResult] = useState(null);
  const scanCode = Number.isInteger(Number(asset.id)) && Number(asset.id) > 0
    ? `PP-A-${Number(asset.id)}` : '';
  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(scanCode);
      setCopyResult({ code: scanCode, message: 'Код скопирован — вставьте его в приложение курьера.' });
    } catch {
      setCopyResult({ code: scanCode, message: 'Выделите код и скопируйте вручную (Ctrl+C).' });
    }
  };

  const handleChange = (field, value) => {
    // Используем onChange правильно - передаем field и value, а не функцию
    onChange(field, value);
  };

  const handleAutocompleteChange = (field) => (event, value) => {
    // Также используем onChange правильно
    onChange(field, value || '');
  };

  // Обработчик изменения статуса
  const handleStatusChange = (event, newValue) => {
    console.log('🎯 Изменение статуса:', newValue);
    
    if (newValue && typeof newValue === 'object') {
      // Если выбран объект статуса
      onChange('asset_status_id', newValue.id);
      onChange('status', newValue.name);
      console.log('✅ Установлен статус:', { id: newValue.id, name: newValue.name });
    } else if (newValue === null) {
      // Если статус очищен
      onChange('asset_status_id', null);
      onChange('status', '');
      console.log('🗑️ Статус очищен');
    }
  };

  // Цвета для статусов
  const getStatusColor = (status) => {
    const statusColors = {
      'доступен': 'success',
      'в аренде': 'warning', 
      'на ремонте': 'error',
      'резерв': 'info',
      'недоступен': 'default'
    };
    return statusColors[status?.toLowerCase()] || 'default';
  };

  return (
    <Paper id="asset-basic" className="asset-section" elevation={0} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
        <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6" component="h2">
          Основная информация
        </Typography>
      </Box>
      

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {scanCode ? (
            <TextField
              fullWidth
              label="Код вещи для курьера"
              value={scanCode}
              onFocus={event => event.target.select?.()}
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">
                  <Button onClick={copyCode}>Копировать</Button>
                </InputAdornment>,
              }}
              helperText={copyResult?.code === scanCode ? copyResult.message
                : 'Введите этот код в приложении курьера → «Забрать» → «Код вещи».'}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Код вещи для курьера появится после сохранения актива.
            </Typography>
          )}
        </Grid>
        {/* Название */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Название актива"
            value={asset.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LabelIcon color="action" />
                </InputAdornment>
              ),
            }}
            helperText="Введите уникальное название актива"
          />
        </Grid>

        {/* SKU */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Артикул (SKU)"
            value={asset.sku || ""}
            onChange={(e) => handleChange("sku", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InfoIcon color="action" />
                </InputAdornment>
              ),
            }}
            helperText="Артикул товара"
          />
        </Grid>

        {/* Категория */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            value={asset.category || null}
            onChange={handleAutocompleteChange('category')}
            options={DEFAULT_CATEGORIES}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label="Категория"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <CategoryIcon color="action" />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
                helperText="Выберите или введите категорию"
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <CategoryIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                {option}
              </Box>
            )}
          />
        </Grid>

        {/* Статус */}
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <Autocomplete
              fullWidth
              options={statuses}
              disabled={asset.status === 'in_transit' || statuses.some(s => s.id === asset.asset_status_id && s.status_key === 'in_transit')}
              getOptionDisabled={option => option.status_key === 'in_transit'}
              getOptionLabel={(option) => option.name || ''}
              value={statuses.find(s => s.id === asset.asset_status_id) || null}
              onChange={handleStatusChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Статус актива"
                  required
                  error={!asset.asset_status_id}
                  helperText={!asset.asset_status_id ? "Выберите статус" : ""}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Chip 
                    label={option.name} 
                    size="small" 
                    color={getStatusColor(option.name)}
                  />
                </li>
              )}
            />
            <Tooltip title="Создать новый статус">
              <IconButton 
                onClick={onCreateStatus} 
                color="primary"
                sx={{ 
                  border: 1, 
                  borderColor: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.lighter' 
                  }
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* Начальное место нового актива; существующие перемещаются ниже. */}
        {!asset.id && <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl fullWidth>
              <InputLabel id="asset-point-label">Пункт проката</InputLabel>
              <Select
                labelId="asset-point-label"
                value={asset.rental_point_id || ""}
                onChange={(e) => {
                  const selectedPoint = rentalPoints.find(p => p.id === e.target.value);
                  handleChange("rental_point_id", e.target.value);
                  handleChange("rental_point", selectedPoint ? selectedPoint.name : "");
                }}
                label="Пункт проката"
              >
                {rentalPoints.map((point) => (
                  <MenuItem key={point.id} value={point.id}>
                    <Box display="flex" alignItems="center">
                      <LocationOnIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                      {point.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Создать новый пункт проката">
              <IconButton 
                onClick={onCreateRentalPoint} 
                color="primary"
                sx={{ 
                  border: 1, 
                  borderColor: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.lighter' 
                  }
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>}

        {!!asset.id && <Grid item xs={12}>
          <AssetLockerPlacement asset={asset} rentalPoints={rentalPoints} onChange={handleChange} />
        </Grid>}

        {/* Инвентарный номер */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Инвентарный номер"
            value={asset.inventory_number || ""}
            onChange={(e) => handleChange("inventory_number", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <InfoIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Тариф */}
        <Grid item xs={12} md={6}>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControl fullWidth>
              <InputLabel id="asset-tariff-label">Тариф</InputLabel>
              <Select
                labelId="asset-tariff-label"
                value={asset.tariff_id || ""}
                onChange={(e) => {
                  const selectedTariff = tariffs.find(t => t.id === e.target.value);
                  handleChange("tariff_id", e.target.value);
                  handleChange("tariff", selectedTariff ? selectedTariff.name : "");
                }}
                label="Тариф"
              >
                {tariffs.map((tariff) => (
                  <MenuItem key={tariff.id} value={tariff.id}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <span>{tariff.name}</span>
                      <Chip 
                        label={`${tariff.price} ₽`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Создать новый тариф">
              <IconButton 
                onClick={onCreateTariff} 
                color="primary"
                sx={{ 
                  border: 1, 
                  borderColor: 'primary.main',
                  '&:hover': { 
                    backgroundColor: 'primary.lighter' 
                  }
                }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>

        {/* Описание */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Описание"
            value={asset.description || ""}
            onChange={(e) => handleChange("description", e.target.value)}
            helperText="Подробное описание актива, особенности, комплектация"
          />
        </Grid>


      </Grid>
      {!separateSections && <><AssetFinancialInfo asset={asset} onChange={onChange} /><AssetDates asset={asset} onChange={onChange} /></>}
    </Paper>
  );
};

export default AssetBasicInfo;

export function AssetFinancialInfo({ asset, onChange: handleChange }) {
  return <Paper id="asset-finance" className="asset-section" elevation={0} sx={{p:3, mb:3}}>
    <Box className="asset-section-title"><MonetizationOnIcon color="primary" /><Typography variant="h6" component="h2">Финансовые параметры</Typography></Box>
    <Grid container spacing={2}>
        {/* Финансовые поля */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Залог"
            value={asset.deposit ?? ""}
            onChange={(e) => handleChange("deposit", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MonetizationOnIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">₽</InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="number"
            label="Цена покупки"
            value={asset.purchase_price ?? ""}
            onChange={(e) => handleChange("purchase_price", e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₽</InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel id="asset-discount-label">Скидка</InputLabel>
            <Select
              labelId="asset-discount-label"
              value={asset.discount || ""}
              onChange={(e) => handleChange("discount", e.target.value)}
              label="Скидка"
            >
              {DEFAULT_DISCOUNTS.map((discount) => (
                <MenuItem key={discount} value={discount}>
                  {discount}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
    </Grid>
  </Paper>;
}

export function AssetDates({ asset, onChange: handleChange }) {
  return <Paper id="asset-dates" className="asset-section" elevation={0} sx={{p:3, mb:3}}>
    <Box className="asset-section-title"><EventOutlinedIcon color="primary" /><Typography variant="h6" component="h2">Даты</Typography></Box>
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Дата покупки"
            value={formatDateForInput(asset.purchase_date) || ""}
            onChange={(e) => handleChange("purchase_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Дата обслуживания"
            value={formatDateForInput(asset.maintenance_due_date) || ""}
            onChange={(e) => handleChange("maintenance_due_date", e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Дата планового обслуживания актива"
          />
        </Grid>


    </Grid>
  </Paper>;
}
